import { Button, Image, Upload, Progress, Tooltip } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import {
    PaperClipOutlined,
    FileImageOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileZipOutlined,
    FileOutlined,
    CloseCircleFilled,
    EyeOutlined,
} from '@ant-design/icons';

export const getFileIcon = (file) => {
    const name = (file?.name || file?.originalName || '').toLowerCase();
    const type = file?.type || file?.fileType || '';

    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name))
        return <FileImageOutlined style={{ color: '#1677ff', fontSize: 18 }} />;
    if (/\.pdf$/.test(name) || type === 'application/pdf')
        return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />;
    if (/\.(docx?)$/.test(name))
        return <FileWordOutlined style={{ color: '#1677ff', fontSize: 18 }} />;
    if (/\.(xlsx?|csv)$/.test(name))
        return <FileExcelOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
    if (/\.(zip|rar|7z|tar|gz)$/.test(name))
        return <FileZipOutlined style={{ color: '#faad14', fontSize: 18 }} />;
    return <FileOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />;
};

export const shortName = (name = '', max = 18) => {
    const parts = name.split('.');
    const ext = parts.length > 1 ? parts.pop() : '';
    const base = parts.join('.');
    if (base.length + ext.length + 1 <= max) return name;
    return `${base.slice(0, max - ext.length - 4)}...${ext ? `.${ext}` : ''}`;
};

export const isImage = (file) => {
    const name = (file?.name || file?.originalName || '').toLowerCase();
    const type = file?.type || file?.fileType || '';
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name);
};

export function PendingFileChip({ file, onRemove, progress }) {
    const preview = file.preview;
    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: '#f0f5ff',
            border: '1px solid #d6e4ff',
            borderRadius: 6,
            padding: '4px 8px',
            maxWidth: 200,
        }}>
            {preview
                ? <img src={preview} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3 }} />
                : getFileIcon(file)
            }
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {shortName(file.name)}
                </div>
                {progress !== undefined && progress < 100 && (
                    <Progress percent={progress} size="small" showInfo={false} strokeWidth={3} />
                )}
            </div>
            {onRemove && (
                <CloseCircleFilled
                    onClick={onRemove}
                    style={{ color: '#bfbfbf', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
                />
            )}
        </div>
    );
}

export function AttachedFileChip({ file }) {
    const img = isImage(file);
    const displayName = (file.name || '').split('---')[0];

    return (
        <Tooltip title={displayName}>
            <div
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    background: '#fafafa',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: '8px 10px',
                    width: 100,
                    cursor: 'pointer',
                }}
                onClick={() => !img && window.open(file.path, '_blank')}
            >
                {img
                    ? <Image
                        src={file.path}
                        alt={displayName}
                        style={{ width: 60, height: 50, objectFit: 'cover', borderRadius: 4 }}
                        preview={{ mask: <EyeOutlined /> }}
                    />
                    : <div style={{ width: 60, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: 4 }}>
                        {getFileIcon(file)}
                    </div>
                }
                <span style={{ fontSize: 10, color: '#595959', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>
                    {shortName(displayName, 16)}
                </span>
                <span style={{ fontSize: 9, color: '#8c8c8c' }}>
                    {moment.utc(file.createdAt).format('DD MMM YY')}
                </span>
            </div>
        </Tooltip>
    );
}

export function ComposedInputBox({ value, onChange, pendingFiles, uploadingIds, onRemoveFile, onBeforeUpload, placeholder }) {
    const [focused, setFocused] = useState(false);

    return (
        <div
            style={{
                border: `1px solid ${focused ? '#1677ff' : '#d9d9d9'}`,
                borderRadius: 8,
                background: '#fff',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: focused ? '0 0 0 2px rgba(22,119,255,0.1)' : 'none',
                overflow: 'hidden',
            }}
        >
            <textarea
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                rows={4}
                style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                    padding: '10px 12px 6px',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    background: 'transparent',
                    boxSizing: 'border-box',
                }}
            />

            {pendingFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 8px' }}>
                    {pendingFiles.map(pf => (
                        <PendingFileChip
                            key={pf.key}
                            file={pf}
                            progress={uploadingIds[pf.key]}
                            onRemove={() => onRemoveFile(pf.key)}
                        />
                    ))}
                </div>
            )}

            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '5px 8px',
                borderTop: '1px solid #f0f0f0',
                background: '#fafafa',
            }}>
                <Upload
                    beforeUpload={onBeforeUpload}
                    showUploadList={false}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.txt,.ppt,.pptx"
                >
                    <Button
                        icon={<PaperClipOutlined />}
                        type="text"
                        size="small"
                        style={{ color: '#595959', fontSize: 12 }}
                    >
                        Attach file
                    </Button>
                </Upload>
                {pendingFiles.length > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#8c8c8c' }}>
                        {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} attached
                    </span>
                )}
            </div>
        </div>
    );
}